import { digitalAdTitle } from '@/components/artifact/adTitle';
import { TYPE_TITLES } from '@/components/artifact/panelMeta';

import type { RequestOwnerActionConfig } from '../../../../../../bridge/types/action.ts';
import type { Artifact } from '../../../../../../bridge/types/artifact.ts';
import type { ProcessSetupSnapshot } from '../../../../../../bridge/types/state.ts';
import type { SetupCardItem } from '../../../../../../bridge/utils/setupCardItems.ts';

import { ownerActionRowTitle } from './ownerActionRowTitle.ts';

/**
 * The row label for a piece of content in the setup card: "Email 1 of 2",
 * "Instagram Carousel", "Digital Ad". Emails and posts are numbered only when
 * the campaign has more than one, so a single email reads "Create Email"
 * rather than "Email 1 of 1".
 */
export const artifactRowTitle = (
	artifact: Artifact,
	{
		emailIndex,
		emailTotal,
		postIndex,
		postTotal,
		fallbackTitle,
	}: {
		emailIndex?: number;
		emailTotal?: number;
		postIndex?: number;
		postTotal?: number;
		fallbackTitle: string;
	},
): string => {
	const isEmail =
		artifact.type === 'email_html' || artifact.type === 'email_plaintext';
	const isPost = artifact.type === 'instagram_post';
	const isCarousel =
		isPost &&
		Math.max(
			(artifact.content as { image_urls?: string[] }).image_urls?.length ?? 0,
			(artifact.content as { slide_prompts?: string[] }).slide_prompts
				?.length ?? 0,
		) > 1;
	const postLabel = isCarousel ? 'Carousel' : 'Post';
	return isEmail && emailIndex && emailTotal && emailTotal > 1
		? `Email ${emailIndex} of ${emailTotal}`
		: isEmail
			? 'Create Email'
			: isPost && postIndex && postTotal && postTotal > 1
				? `${postLabel} ${postIndex} of ${postTotal}`
				: isPost
					? isCarousel
						? 'Instagram Carousel'
						: 'Instagram Post'
					: // Every ad in a campaign would otherwise render the same type
						// label, leaving the customer no way to say which one they mean.
						// Their headline is what they read off the image.
						artifact.type === 'digital_ad'
						? digitalAdTitle(
								artifact.content,
								TYPE_TITLES[artifact.type] ?? fallbackTitle,
							)
						: (TYPE_TITLES[artifact.type] ?? fallbackTitle);
};

/**
 * The label for a setup step, named exactly as its own row names it. Anything
 * that lists outstanding steps elsewhere in the card goes through here, so the
 * list and the rows can't call the same step two different things.
 */
export const setupItemLabel = (
	snapshot: ProcessSetupSnapshot,
	item: SetupCardItem,
): string => {
	if (item.kind === 'recipients') return 'Choose Recipients';

	if (item.kind === 'action') {
		const action = (snapshot.actions ?? []).find((a) => a.id === item.id);
		const config = action?.config as RequestOwnerActionConfig | undefined;
		return ownerActionRowTitle(config?.instruction, 'Your part');
	}

	const children = snapshot.children ?? [];
	const artifact = children.find((c) => c.id === item.id);
	if (!artifact) return 'Item';

	const isEmail =
		artifact.type === 'email_html' || artifact.type === 'email_plaintext';
	const isPost = artifact.type === 'instagram_post';
	const emails = children.filter(
		(c) => c.type === 'email_html' || c.type === 'email_plaintext',
	);
	const posts = children.filter((c) => c.type === 'instagram_post');

	return artifactRowTitle(artifact, {
		emailIndex: isEmail
			? emails.findIndex((e) => e.id === artifact.id) + 1
			: undefined,
		emailTotal: isEmail ? emails.length : undefined,
		postIndex: isPost
			? posts.findIndex((p) => p.id === artifact.id) + 1
			: undefined,
		postTotal: isPost ? posts.length : undefined,
		fallbackTitle: 'Item',
	});
};
